import Select, { StylesConfig } from 'react-select';
import styled from 'styled-components';

import { colors } from 'styles/theme/colors';
import { LabelSmallBold } from 'styles/typography';
import GearIcon from 'svg/GearIcon';
import SortIcon from 'svg/SortIcon';

import { TaskSort } from './taskCards';

const SORT_OPTIONS: { value: TaskSort; label: string }[] = [
  { value: 'newest', label: 'Newest' },
  { value: 'oldest', label: 'Oldest' },
];

interface ToolbarProps {
  // The sort control renders only when given a handler — the Portal Scans tab shares the bar
  // for `Manage Connected Portals` but has no sort of its own yet.
  sort?: TaskSort;
  onSortChange?: (sort: TaskSort) => void;
  onManagePortals?: () => void;
}

export default function TasksToolbar({ sort, onSortChange, onManagePortals }: ToolbarProps) {
  return (
    <Bar>
      {onSortChange ? (
        <SortGroup data-testid='task-sort'>
          <SortIcon />
          <SortLabel htmlFor='task-sort-input'>Sort By:</SortLabel>
          <Select
            inputId='task-sort-input'
            options={SORT_OPTIONS}
            value={SORT_OPTIONS.find((option) => option.value === sort)}
            onChange={(option) => option && onSortChange(option.value)}
            isSearchable={false}
            styles={sortSelectStyles}
            components={{ IndicatorSeparator: null, DropdownIndicator: null }}
          />
        </SortGroup>
      ) : (
        <span />
      )}
      {onManagePortals && (
        <ManageButton type='button' onClick={onManagePortals} data-testid='manage-connected-portals'>
          <GearIcon width={12} height={12} />
          Manage Connected Portals
        </ManageButton>
      )}
    </Bar>
  );
}

// Drawn as plain bold text in the band; the menu is the styled react-select one rather than the
// browser's own popup.
const sortSelectStyles: StylesConfig<{ value: TaskSort; label: string }, false> = {
  control: (provided) => ({
    ...provided,
    minHeight: 0,
    height: 'auto',
    border: 'none',
    boxShadow: 'none',
    background: 'transparent',
    cursor: 'pointer',
  }),
  valueContainer: (provided) => ({ ...provided, padding: 0 }),
  singleValue: (provided) => ({ ...provided, margin: 0, fontSize: 12, fontWeight: 600, color: colors.black100 }),
  input: (provided) => ({ ...provided, margin: 0, padding: 0 }),
  menu: (provided) => ({ ...provided, minWidth: 120, fontSize: 12 }),
};

const Bar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  /* Bleed across Page.SubHeader's 24px gutters so the band runs edge to edge, as drawn — and
     sit flush against the header block above rather than taking the SubHeader's default
     first-child top margin. */
  padding: 10px 24px;
  margin: 0 -24px;

  &:first-child {
    margin-top: 0;
  }

  background-color: ${({ theme }) => theme.colors.black5};
`;

const SortGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  color: ${({ theme }) => theme.colors.black75};
`;

const SortLabel = styled(LabelSmallBold)`
  color: ${({ theme }) => theme.colors.black75};
`;

const ManageButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-family: ${({ theme }) => theme.textStyles.base.fontFamily};
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.blue100};
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
`;
